import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { useI18n } from '@/app/lib/i18n'
import { contentText, contentTextNode } from '@/app/lib/i18n/contentText'
import { ImageLoader } from '@/ui/components/ImageLoader'
import * as styles from './CoursePage.css'

const FALLBACK_HERO = '/images/certification/lesson-1.png'

function ArrowLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

function ArrowRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  )
}

export interface CoursePageHeroProps {
  title: string
  heroImageUrl?: string | null
  /** When true, show Continue instead of Begin. */
  hasProgress?: boolean
  /** Title of the next incomplete (or first) lesson for the Continue CTA. */
  nextLessonTitle?: string | null
  /**
   * When false, renders a non-navigating preview that keeps hover styles
   * (back + begin controls do nothing).
   */
  interactive?: boolean
  onBeginOrContinue?: () => void
}

export function CoursePageHero({
  title,
  heroImageUrl,
  hasProgress = false,
  nextLessonTitle,
  interactive = true,
  onBeginOrContinue,
}: CoursePageHeroProps) {
  const { t, tx } = useI18n()
  const displayTitle = title.trim() ? contentTextNode(tx, title) : '—'
  const imageSrc = heroImageUrl || FALLBACK_HERO

  const resolvedNext = nextLessonTitle ? contentText(t, nextLessonTitle).trim() : ''
  const ctaLabel = hasProgress && resolvedNext
    ? t('certification.course.continueCourseNamed', { title: resolvedNext })
    : hasProgress
      ? t('certification.course.continueCourse')
      : t('certification.course.beginCourse')

  const backLabel = t('certification.course.back').replace(/^[←<\u2190\-–—]+\s*/u, '').trim() || 'Back'

  const back = interactive ? (
    <Link to="/" className={styles.heroBack}>
      <ArrowLeftIcon />
      <span>{backLabel}</span>
    </Link>
  ) : (
    <span className={styles.heroBack}>
      <ArrowLeftIcon />
      <span>{backLabel}</span>
    </span>
  )

  const begin = interactive ? (
    <button type="button" className={styles.heroBtn} onClick={() => onBeginOrContinue?.()}>
      <span className={styles.heroBtnLabel}>{ctaLabel}</span>
      <ArrowRightIcon />
    </button>
  ) : (
    <div className={styles.heroBtn}>
      <span className={styles.heroBtnLabel}>{ctaLabel}</span>
      <ArrowRightIcon />
    </div>
  )

  return (
    <div className={styles.heroWrap}>
      <div className={styles.hero}>
        <div className={styles.heroImg}>
          <ImageLoader src={imageSrc} />
        </div>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          {back}
          <div className={styles.heroBottom}>
            <h1 className={styles.heroTitle}>{displayTitle}</h1>
            {begin}
          </div>
        </div>
      </div>
    </div>
  )
}
